import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, type CompetitionDetail, type ResultsGrid } from "../../lib/api";
import type { LiveCompetition } from "../../lib/live";
import { analytics, AnalyticsEvent } from "../../lib/analytics";
import { trackApiError } from "../../lib/analytics/errorTracking";
import { useTheme } from "../../lib/theme";
import { buildExportData } from "../../lib/export/gridExport";
import { downloadCanvasAsPng, renderResultsImage } from "../../lib/export/exportImage";
import {
  Avatar,
  Badge,
  Button,
  Crest,
  EmptyState,
  ErrorNote,
  Icon,
  Modal,
  Skeleton,
  cx,
  useToast,
} from "../../components/ui";

type Cell = ResultsGrid["cells"][number];

/**
 * What the grid is doing, and a way to hurry it along.
 *
 * The grid updates itself — the page polls a cheap endpoint and re-fetches this when the
 * server says something changed. But a player watching a match that has just ended wants to
 * know the screen is actually live rather than merely old, and wants a button when it isn't
 * fast enough. Saying so is cheaper than being asked.
 */
function LiveStatus({ live }: { live: LiveCompetition }) {
  const toast = useToast();
  const [now, setNow] = useState(() => Date.now());

  // Only ticks while there is a relative time on screen, and only once a minute.
  useEffect(() => {
    if (!live.lastChangeAt) return;
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, [live.lastChangeAt]);

  async function refresh() {
    const outcome = await live.refreshNow();
    if (!outcome) {
      toast.show("Couldn't reach the server — try again in a moment.");
      return;
    }
    if (outcome.roundsSettled > 0) {
      toast.show(outcome.roundsSettled === 1 ? "Round settled" : `${outcome.roundsSettled} rounds settled`);
    } else if (outcome.picksDecided > 0) {
      toast.show(`${outcome.picksDecided} result${outcome.picksDecided === 1 ? "" : "s"} in`);
    } else {
      toast.show("Up to date");
    }
  }

  return (
    <div className="flex items-center gap-2">
      {live.isLive && (
        <Badge tone="accent">
          <span
            className="size-1.5 animate-pulse rounded-full bg-current"
            aria-hidden="true"
          />
          Live
        </Badge>
      )}
      {live.lastChangeAt && (
        <span className="hidden text-xs text-fg-faint sm:inline" aria-live="polite">
          Updated {relativeTime(live.lastChangeAt, now)}
        </span>
      )}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => void refresh()}
        loading={live.isRefreshing}
        aria-label="Refresh results"
      >
        {!live.isRefreshing && <Icon name="refresh" className="size-4" />}
        <span className="hidden sm:inline">Refresh</span>
      </Button>
    </div>
  );
}

/** Coarse on purpose — "just now" for the first minute is what anyone actually means. */
function relativeTime(at: Date, now: number): string {
  const minutes = Math.floor((now - at.getTime()) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes === 1) return "1 min ago";
  if (minutes < 60) return `${minutes} mins ago`;
  const hours = Math.floor(minutes / 60);
  return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
}


/**
 * Every player, every round, at a glance — who's out, what they picked (once it's revealed) and
 * how it went. Visible to everyone in the competition, not just the admin: this is the shared
 * record of the competition, not a management tool, though the admin gets one extra ability —
 * setting a pick directly from a cell in the currently open round.
 *
 * Masking follows the same reveal rule as the pick tab and everyone else's picks — nobody gets
 * to peek early here either, admin included.
 *
 * Outcome is never carried by colour alone: a win and a loss also differ by glyph, so the grid
 * still reads correctly in greyscale, in a screenshot, or to a colour-blind player.
 */
export function ResultsGridPanel({
  competition,
  live,
}: {
  competition: CompetitionDetail;
  /**
   * The page's live signal. Optional so the panel still renders standalone — without it the
   * grid is simply a normal query, which is the correct fallback rather than a broken view.
   */
  live?: LiveCompetition;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["results-grid", competition.id],
    queryFn: () => api.competitions.resultsGrid(competition.id),
  });

  const [settingPick, setSettingPick] = useState<{
    entryId: string;
    entryName: string;
    roundId: string;
    roundNumber: number;
  } | null>(null);

  const sortedEntries = useMemo(() => {
    if (!data) return [];
    return [...data.entries].sort((a, b) => {
      if (a.status !== b.status) return a.status === "alive" ? -1 : 1;
      return a.displayName.localeCompare(b.displayName);
    });
  }, [data]);

  const cellByKey = useMemo(() => {
    if (!data) return new Map<string, Cell>();
    return new Map(data.cells.map((cell) => [`${cell.entryId}:${cell.roundId}`, cell]));
  }, [data]);

  if (isLoading) return <Skeleton className="h-96" />;
  if (error) {
    return (
      <ErrorNote>
        {error instanceof ApiError ? error.message : "Couldn't load the results grid."}
      </ErrorNote>
    );
  }
  if (!data || !data.entries.length) return <EmptyState title="Nobody has entered yet" />;
  if (!data.rounds.length) {
    return (
      <EmptyState
        icon={<Icon name="calendar" className="size-7" />}
        title="No rounds yet"
        description="Once fixtures are imported for this league, this fills in round by round."
      />
    );
  }

  const isAdmin = competition.role === "admin";
  // Admins can override any pick a player made (or missed) while still in: the server unwinds
  // and re-settles a round that was already decided.
  const roundNumberById = new Map(data.rounds.map((r) => [r.id, r.roundNumber]));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-xl text-sm leading-relaxed text-fg-subtle">
          {isAdmin
            ? "Every player's history. Tap a cell to set or override that player's pick."
            : "Every player's history — picks reveal for everyone at the same moment, admin included."}
        </p>
        <div className="flex items-center gap-2">
          {live && <LiveStatus live={live} />}
          <ExportButton competition={competition} grid={data} />
        </div>
      </div>

      <div className="card overflow-hidden p-0">
        <div className="mobile-scroll-tabs overflow-x-auto">
          <table className="w-full min-w-max border-separate border-spacing-0 text-sm">
            <thead>
              <tr>
                <th className="sticky left-0 z-20 min-w-[11rem] border-b border-line bg-surface px-4 py-3 text-left">
                  <span className="label">Player</span>
                </th>
                {data.rounds.map((round) => (
                  <th
                    key={round.id}
                    className="min-w-[4.75rem] border-b border-l border-line bg-surface px-2 py-3 text-center"
                  >
                    <span className="tabular block text-[13px] font-bold text-fg">
                      R{round.roundNumber}
                    </span>
                    <span
                      className={cx(
                        "mt-0.5 block text-[10px] font-medium",
                        round.status === "open" ? "text-accent-text" : "text-fg-faint",
                      )}
                    >
                      {round.status}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {sortedEntries.map((entry, rowIndex) => {
                const last = rowIndex === sortedEntries.length - 1;
                return (
                  <tr
                    key={entry.entryId}
                    className={cx("group", entry.status === "out" && "opacity-65")}
                  >
                    <td
                      className={cx(
                        "sticky left-0 z-10 bg-surface px-4 py-2.5 transition-colors group-hover:bg-tint",
                        !last && "border-b border-line",
                      )}
                    >
                      <span className="flex items-center gap-2.5">
                        <Avatar
                          name={entry.displayName}
                          color={entry.avatarColor}
                          size={30}
                          dimmed={entry.status === "out"}
                        />
                        <span className="min-w-0">
                          <span className="block max-w-[7.5rem] truncate text-[13px] font-semibold text-fg">
                            {entry.displayName}
                          </span>
                          <span
                            className={cx(
                              "text-[11px] font-semibold uppercase tracking-wide",
                              entry.status === "alive" ? "text-accent-text" : "text-danger-text",
                            )}
                          >
                            {entry.status}
                          </span>
                        </span>
                      </span>
                    </td>

                    {data.rounds.map((round) => {
                      const cell = cellByKey.get(`${entry.entryId}:${round.id}`);
                      const outAt = entry.eliminatedRoundId
                        ? roundNumberById.get(entry.eliminatedRoundId)
                        : undefined;
                      const clickable =
                        isAdmin &&
                        ["open", "locked", "settled"].includes(round.status) &&
                        (entry.status === "alive" ||
                          (outAt !== undefined && round.roundNumber <= outAt));
                      return (
                        <td
                          key={round.id}
                          className={cx(
                            "border-l border-line p-1.5 transition-colors group-hover:bg-tint",
                            !last && "border-b",
                          )}
                        >
                          <GridCell
                            cell={cell}
                            clickable={clickable}
                            onClick={() =>
                              setSettingPick({
                                entryId: entry.entryId,
                                entryName: entry.displayName,
                                roundId: round.id,
                                roundNumber: round.roundNumber,
                              })
                            }
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Legend />

      {settingPick && (
        <SetPickModal
          competitionId={competition.id}
          {...settingPick}
          onClose={() => setSettingPick(null)}
        />
      )}
    </div>
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-fg-subtle">
      <LegendItem className="border-accent-line bg-accent-soft text-accent-text" icon="check" label="Win" />
      <LegendItem className="border-danger-line bg-danger-soft text-danger-text" icon="close" label="Loss" />
      <LegendItem
        className="border-warn-line bg-warn-soft text-warn-text"
        icon="minus"
        label="Void — survived"
      />
      <LegendItem className="border-line bg-tint text-fg-faint" icon="clock" label="Pending" />
      <span className="flex items-center gap-1.5">
        <span className="grid size-5 place-items-center rounded-md border border-line bg-tint text-[10px] tracking-tight text-fg-faint">
          •••
        </span>
        Hidden until reveal
      </span>
    </div>
  );
}

function LegendItem({
  className,
  icon,
  label,
}: {
  className: string;
  icon: "check" | "close" | "clock" | "minus";
  label: string;
}) {
  return (
    <span className="flex items-center gap-1.5">
      <span className={cx("grid size-5 place-items-center rounded-md border", className)}>
        <Icon name={icon} className="size-3" strokeWidth={2.6} />
      </span>
      {label}
    </span>
  );
}

/** Reads as a sentence in a tooltip; "void" alone doesn't tell anyone what happened to them. */
const OUTCOME_LABEL: Record<string, string> = {
  win: " — won, survived",
  loss: " — lost",
  void: " — fixture voided, survived and the team went back in the pool",
  pending: "",
};

function GridCell({
  cell,
  clickable,
  onClick,
}: {
  cell: Cell | undefined;
  clickable: boolean;
  onClick: () => void;
}) {
  const hidden = Boolean(cell?.hasPicked && !cell.team);
  const outcome = cell?.team ? cell.outcome : null;

  // A voided pick is its own outcome, not a pending one: the player did pick, the fixture
  // didn't count, and they survived. Before voided picks were kept, this cell was simply
  // blank — so the round they survived looked like a round they never entered.
  const tone =
    outcome === "win"
      ? "border-accent-line bg-accent-soft"
      : outcome === "loss"
        ? "border-danger-line bg-danger-soft"
        : outcome === "void"
          ? "border-warn-line bg-warn-soft"
          : cell?.team
            ? "border-line bg-tint"
            : "border-line/60 bg-transparent";

  return (
    <button
      type="button"
      disabled={!clickable}
      onClick={onClick}
      title={
        clickable
          ? "Set this player's pick"
          : cell?.team
            ? `${cell.team.name}${OUTCOME_LABEL[outcome ?? "pending"] ?? ""}`
            : undefined
      }
      className={cx(
        "relative flex h-[4.25rem] w-[4.25rem] flex-col items-center justify-center gap-1 rounded-xl border transition-all duration-200",
        tone,
        clickable
          ? "cursor-pointer hover:border-accent-line hover:bg-accent-soft"
          : "cursor-default",
      )}
    >
      {/* Outcome glyph, so win/loss never depends on colour alone. */}
      {outcome === "win" && (
        <Icon
          name="check"
          className="absolute right-1 top-1 size-3 text-accent-text"
          strokeWidth={3}
          label="Won"
        />
      )}
      {outcome === "loss" && (
        <Icon
          name="close"
          className="absolute right-1 top-1 size-3 text-danger-text"
          strokeWidth={3}
          label="Lost"
        />
      )}
      {outcome === "void" && (
        <Icon
          name="minus"
          className="absolute right-1 top-1 size-3 text-warn-text"
          strokeWidth={3}
          label="Voided — survived"
        />
      )}

      {hidden ? (
        <span className="text-[13px] tracking-widest text-fg-faint">•••</span>
      ) : cell?.team ? (
        <>
          <Crest url={cell.team.crestUrl} name={cell.team.name} size={22} />
          <span className="max-w-[3.5rem] truncate text-[10px] font-semibold text-fg-muted">
            {cell.team.shortName}
          </span>
        </>
      ) : clickable ? (
        <Icon name="plus" className="size-4 text-fg-faint" />
      ) : (
        <span className="text-fg-faint">—</span>
      )}
    </button>
  );
}

/* Admin: set someone's pick ------------------------------------------------ */

function SetPickModal({
  competitionId,
  entryId,
  entryName,
  roundId,
  roundNumber,
  onClose,
}: {
  competitionId: string;
  entryId: string;
  entryName: string;
  roundId: string;
  roundNumber: number;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data: teams, isLoading } = useQuery({
    queryKey: ["admin-teams", competitionId, roundId, entryId],
    queryFn: () => api.competitions.adminTeams(competitionId, roundId, entryId),
  });

  const mutation = useMutation({
    mutationFn: (teamId: string) =>
      api.competitions.adminPick(competitionId, roundId, entryId, teamId),
    onSuccess: (_result, teamId) => {
      analytics.track(AnalyticsEvent.PickSubmitted, { competitionId, roundId, setByAdmin: true });
      const team = teams?.find((t) => t.teamId === teamId);
      toast.show(`${entryName}'s pick set${team ? ` — ${team.name}` : ""}`);
      void queryClient.invalidateQueries({ queryKey: ["results-grid", competitionId] });
      void queryClient.invalidateQueries({ queryKey: ["picks", competitionId, roundId] });
      void queryClient.invalidateQueries({ queryKey: ["competition", competitionId] });
      void queryClient.invalidateQueries({ queryKey: ["leaderboard", competitionId] });
      onClose();
    },
    onError: (error) => trackApiError(error, "admin_set_pick"),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={`Set ${entryName}'s pick`}
      description={`Round ${roundNumber}. Replaces any pick they have — after the deadline the round is re-decided from this one. Team rules still apply, and it's recorded in the audit log.`}
    >
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-14" />
          ))}
        </div>
      ) : !teams?.length ? (
        <EmptyState title="No teams available" description="Nothing's playing in this round yet." />
      ) : (
        <div className="space-y-2">
          {teams.map((team) => (
            <button
              key={team.teamId}
              type="button"
              disabled={team.used || mutation.isPending}
              onClick={() => mutation.mutate(team.teamId)}
              className={cx(
                "flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-all duration-200",
                team.used
                  ? "cursor-not-allowed border-line bg-tint opacity-50"
                  : "border-line bg-surface hover:border-accent-line hover:bg-accent-soft",
              )}
            >
              <Crest url={team.crestUrl} name={team.name} size={32} />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium text-fg">{team.name}</span>
                <span className="block truncate text-[12px] text-fg-subtle">
                  {team.isHome ? "vs" : "at"} {team.opponentName}
                </span>
              </span>
              {team.used && <Badge tone="neutral">Used</Badge>}
            </button>
          ))}
        </div>
      )}

      {mutation.error && (
        <div className="mt-4">
          <ErrorNote>
            {mutation.error instanceof ApiError ? mutation.error.message : "Couldn't set that pick."}
          </ErrorNote>
        </div>
      )}
    </Modal>
  );
}

/* Export ------------------------------------------------------------------- */

function ExportButton({
  competition,
  grid,
}: {
  competition: CompetitionDetail;
  grid: ResultsGrid;
}) {
  const { resolved } = useTheme();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  async function exportImage() {
    setBusy(true);
    try {
      const data = buildExportData(competition, grid);
      // Rendered in whichever theme they're looking at — a dark screenshot dropped into a
      // group chat should look like the app they just came from.
      const canvas = renderResultsImage(data, resolved);
      await downloadCanvasAsPng(canvas, `${competition.slug || "results"}-results.png`);
      analytics.track(AnalyticsEvent.CompetitionSettingsUpdated, {
        competitionId: competition.id,
        field: "export_image",
      });
      toast.show("Image saved");
    } catch (err) {
      trackApiError(err, "export_results_image");
      toast.show("Couldn't generate the image", "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Button size="sm" variant="secondary" loading={busy} onClick={() => void exportImage()}>
      <Icon name="download" className="size-4" /> Share image
    </Button>
  );
}
